import { zodResolver } from "@hookform/resolvers/zod";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { authClient } from "@/auth";
import { AlertBar } from "@/components/AlertBar";
import { Button } from "@/components/catalyst/Button";
import {
	ErrorMessage,
	Field,
	FieldGroup,
	Label,
} from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { setBetterAuthError } from "@/hooks/use-better-auth-error";
import { useTurnstile } from "@/hooks/use-turnstile";
import { type Schema, schema } from "./schema";

const route = getRouteApi("/auth/password-reset");

export function Form() {
	const navigate = useNavigate();
	const { token } = route.useSearch();
	const { getCaptchaHeaders } = useTurnstile();

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<Schema>({
		resolver: zodResolver(schema),
	});

	if (!token) {
		navigate({ to: "/auth/signin" });
		return;
	}

	const setServerError = setBetterAuthError(setError);

	const onSubmit = handleSubmit((data) => {
		authClient.resetPassword({
			newPassword: data.password,
			token,
			fetchOptions: {
				headers: getCaptchaHeaders(),
				onSuccess: () => {
					toast.success("Your password has been reset!", {
						id: "password-reset",
					});
					navigate({
						to: "/auth/signin",
						search: { success: "password-reset" },
					});
				},
				onError: (ctx) => {
					setServerError(ctx.error.code);
				},
			},
		});
	});

	return (
		<form className="space-y-6" onSubmit={onSubmit}>
			{errors.root?.message && (
				<AlertBar title="Woops!" variant="error">
					{errors.root?.message}
				</AlertBar>
			)}
			<FieldGroup>
				<Field>
					<Label>New Password</Label>
					<Input
						type="password"
						{...register("password")}
						placeholder="Password"
					/>
					{errors.password?.message && (
						<ErrorMessage>{errors.password.message}</ErrorMessage>
					)}
				</Field>
				<Field>
					<Label>Confirm Password</Label>
					<Input
						type="password"
						{...register("passwordConfirmation")}
						placeholder="Confirm Password"
					/>
					{errors.password?.message && (
						<ErrorMessage>{errors.passwordConfirmation?.message}</ErrorMessage>
					)}
				</Field>
			</FieldGroup>

			<Button
				type="submit"
				color="dark/white"
				className="w-full"
				disabled={isSubmitting}
			>
				Reset Password
			</Button>
		</form>
	);
}
